import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Constants} from '../config/constants.config';
import {Observable} from 'rxjs';
import {
    InBodyDto,
    MasterAutoDiaryGenerationConfigViewModel, MasterAutoDiaryRecipesDto,
    MasterProfilesCountViewModel,
    MasterProfileViewModel
} from '../viewModels/master.viewModel';
import {AdminLoginUser, Nutrients, Token, UserProfileViewModel, UserWeightHistory} from '../viewModels/userProfile.viewModel';
import {map} from 'rxjs/operators';
import {MealViewModel} from '../viewModels/meal.viewModel';
import {DailyMacrosViewModel, DailyMealViewModel, MacroOverwrites} from '../viewModels/dailyMeal.viewModel';
import {SubscriptionViewModel} from '../viewModels/subscription.viewModel';
import {DiaryChatMessages, DiaryViewModel} from '../viewModels/diary.viewModel';
import {formatDate} from '../utilities/date.utilities';
import {DiarySummaryClientsViewModel, DiarySummaryViewModel} from '../viewModels/diarySummary.viewModel';

export class MealWithDailyMealViewModel extends MealViewModel {
    dailyMeal?: DailyMealViewModel;
}

export class MealOverwriteViewModel {
    id?: number;
    percentage?: number;
    meal?: MealWithDailyMealViewModel;
}

export class MasterNote {
    id?: number;
    title?: string;
    text?: string;
    created_at?: string;
}

@Injectable()
export class MasterService {

    constructor(
        private readonly httpClient: HttpClient,
        private readonly constants: Constants,
    ) {

    }

    getProfiles(skip: number, per_page: number, search: string): Observable<MasterProfileViewModel> {
        let strs = `skip=${skip}&per_page=${per_page}`;

        if (search != null && search.length >= 1) {
            strs += `&search=${search}`;
        }

        return this.httpClient.get<MasterProfileViewModel>(`${this.constants.apiURL()}/master/profiles/?${strs}`);
    }

    getProfilesCount(): Observable<MasterProfilesCountViewModel> {
        return this.httpClient.get<MasterProfilesCountViewModel>(`${this.constants.apiURL()}/master/profiles/count/`);
    }

    getUserProfile(id: number): Observable<UserProfileViewModel> {
        return this.httpClient.post<UserProfileViewModel>(`${this.constants.apiURL()}/master/profiles/single/`, {
            id: id
        });
    }

    updateUserProfile(userProfile: UserProfileViewModel): Observable<UserProfileViewModel> {
        return this.httpClient.put<UserProfileViewModel>(`${this.constants.apiURL()}/master/profiles/single/`, userProfile)
            .pipe(
                // tslint:disable-next-line:no-shadowed-variable
                map(userProfile => {
                    return userProfile;
                })
            );
    }

    createProfile(first_name: string, last_name: string, email: string, password: string): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/master/profiles/`, {
            email: email,
            password: password,
            first_name: first_name,
            last_name: last_name
        });
    }

    addSubAccount(email: string): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/master/profile/add-subaccount/`, {
            email: email
        });
    }

    sendLoginInfo(email: string, password: string, text: string, files: File[]): Observable<void> {
        const formData: FormData = new FormData();

        for (var x = 0; x < files.length; x++) {
            formData.append('file_' + files[x].name, files[x], files[x].name);
        }

        formData.append('email', email + '');
        formData.append('password', password + '');
        formData.append('text', text + '');

        return this.httpClient.post<void>(`${this.constants.apiURL()}/master/profile/send/login/info/`, formData);
    }

    masterLogin(user_id): Observable<Token> {
        return this.httpClient.post<Token>(`${this.constants.apiURL()}/master/login/as/`, {
            'user_id': user_id
        });
    }

    getMealOverwrites(user_id: number): Observable<MealOverwriteViewModel[]> {
        return this.httpClient.post<MealOverwriteViewModel[]>(`${this.constants.apiURL()}/master/meals/overwrites/`, {
            id: user_id
        });
    }

    getMealsWithDailyMeals(user_id: number): Observable<MealWithDailyMealViewModel[]> {
        return this.httpClient.post<MealWithDailyMealViewModel[]>(`${this.constants.apiURL()}/master/meals/daily/meals/`, {
            id: user_id
        });
    }

    updateMealsWithDailyMeals(user_id: number, overwrites: MealOverwriteViewModel[]): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/master/meals/overwrites/`, {
            id: user_id,
            overwrites: overwrites
        });
    }

    checkIsSubAccountUnderMaster(): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/master/profile/check/sub/profile/`, {});
    }

    getSubscriptions(id: number): Observable<SubscriptionViewModel[]> {
        return this.httpClient.post<SubscriptionViewModel[]>(`${this.constants.apiURL()}/master/subscriptions/`, {
            id: id
        });
    }

    getSingleSubscription(id: number, subscriptionId: number): Observable<SubscriptionViewModel> {
        return this.httpClient.post<SubscriptionViewModel>(`${this.constants.apiURL()}/master/subscriptions/single/`, {
            id: id,
            subscription_id: subscriptionId
        });
    }

    deleteSubscription(id: number, subscriptionId: number): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/master/subscriptions/delete/`, {
            id: id,
            subscription_id: subscriptionId
        });
    }

    createSubscription(id: number, status, from_date, to_date): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/master/subscriptions/create/`, {
            id: id,
            status: status,
            from_date: from_date,
            to_date: to_date
        });
    }

    updateSubscription(id: number, subscription_id: number, status, from_date, to_date): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/master/subscriptions/`, {
            id: id,
            subscription_id: subscription_id,
            status: status,
            from_date: from_date,
            to_date: to_date
        });
    }

    getNotes(id: number): Observable<MasterNote[]> {
        return this.httpClient.post<MasterNote[]>(`${this.constants.apiURL()}/master/notes/`, {
            id: id
        });
    }

    deleteNote(id: number, noteId: number): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/master/notes/delete/`, {
            id: id,
            note_id: noteId
        });
    }

    getSingleNote(id: number, noteId: number): Observable<MasterNote> {
        return this.httpClient.post<MasterNote>(`${this.constants.apiURL()}/master/notes/single/`, {
            id: id,
            note_id: noteId
        });
    }

    createNote(id: number, title, text): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/master/notes/create/`, {
            id: id,
            title: title,
            text: text,
        });
    }

    updateNote(id: number, note_id: number, title, text): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/master/notes/`, {
            id: id,
            note_id: note_id,
            title: title,
            text: text
        });
    }

    getOverwrites(user_id: number): Observable<MacroOverwrites> {
        return this.httpClient.post<MacroOverwrites>(`${this.constants.apiURL()}/master/user/overwrites/macros/`, {
            user_id: user_id,
        });
    }

    updateOverwrites(user_id: number, overwrites: DailyMacrosViewModel[], daileMealPercentageOverwrites: {}): Observable<void> {
        const newOverwrites = [];

        overwrites.forEach(overwrite => {
            const mealPercentageOverwrites = [];

            if (daileMealPercentageOverwrites[overwrite.id] !== undefined) {
                daileMealPercentageOverwrites[overwrite.id].forEach(percentageOverwrite => {
                    mealPercentageOverwrites.push({
                        meal_id: percentageOverwrite.meal.id,
                        overwrite_percentage: percentageOverwrite.overwrite_percentage
                    });
                });
            }

            newOverwrites.push({
                day: overwrite.day,
                daily_meal_id: overwrite.daily_meal_id,
                overwrite_all_calories: overwrite.overwrite_all_calories,
                overwrite_protein: overwrite.overwrite_protein,
                overwrite_carbs: overwrite.overwrite_carbs,
                overwrite_fat: overwrite.overwrite_fat,
                daily_meal_macro_overwrites: mealPercentageOverwrites
            });
        });

        return this.httpClient.put<void>(`${this.constants.apiURL()}/master/user/overwrites/macros/`, {
            user_id: user_id,
            overwrites: newOverwrites
        });
    }

    uploadInBodyXlsxFile(user_id: number, fileToUpload: File): Observable<void> {
        const formData: FormData = new FormData();
        formData.append('file', fileToUpload, fileToUpload.name);
        formData.append('user_id', user_id + '');

        return this.httpClient.post<void>(`${this.constants.apiURL()}/master/inbody/xlsx/`, formData);
    }

    changeMasterReadyProfile(user_id): Observable<Token> {
        return this.httpClient.post<Token>(`${this.constants.apiURL()}/master/profile/change/master/ready/`, {
            'user_id': user_id
        });
    }

    getMasterNotReadyProfile(): Observable<UserProfileViewModel[]> {
        return this.httpClient.get<UserProfileViewModel[]>(`${this.constants.apiURL()}/master/profile/change/master/ready/`);
    }

    getUnSeenMessages(): Observable<DiaryChatMessages> {
        return this.httpClient.get<DiaryChatMessages>(`${this.constants.apiURL()}/diary/chat/master/not/seen/`);
    }

    getWeightHistory(user_id): Observable<UserWeightHistory[]> {
        return this.httpClient.post<UserWeightHistory[]>(`${this.constants.apiURL()}/master/profile/weight/history/`, {
            'user_id': user_id
        });
    }

    getInBodyHistory(user_id): Observable<InBodyDto> {
        return this.httpClient.get<InBodyDto>(`${this.constants.dataURL()}/v1/inbody/${user_id}`);
    }

    getNutrients(user_id, date_from, date_to): Observable<Nutrients[]> {
        return this.httpClient.post<Nutrients[]>(`${this.constants.apiURL()}/master/profile/nutrients/`, {
            'user_id': user_id,
            'date_from': date_from,
            'date_to': date_to,
        });
    }

    getDiarySummary(masterUserId: number, dateFrom: string, dateTo: string, userClientIds: number[]): Observable<DiarySummaryViewModel> {
        return this.httpClient.post<DiarySummaryViewModel>(`${this.constants.dataURL()}/v2/diary/master/summary`, {
            'masterUserId': masterUserId,
            'fromDate': dateFrom,
            'toDate': dateTo,
            'userClientIds': userClientIds
        });
    }

    getDiarySummaryClients(masterUserId: number, dateFrom: string, dateTo: string): Observable<DiarySummaryClientsViewModel> {
        return this.httpClient.post<DiarySummaryClientsViewModel>(`${this.constants.dataURL()}/v2/diary/master/summary/clients`, {
            'masterUserId': masterUserId,
            'fromDate': dateFrom,
            'toDate': dateTo
        });
    }

    updateDiarySummaryClients(masterUserId: number, dateFrom: string, dateTo: string, userClientIds: number[]): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.dataURL()}/v2/diary/master/summary/clients`, {
            'masterUserId': masterUserId,
            'fromDate': dateFrom,
            'toDate': dateTo,
            'userClientIds': userClientIds
        });
    }

    getMasterAutoDiaryGenerationConfigs(): Observable<MasterAutoDiaryGenerationConfigViewModel[]> {
        return this.httpClient.get<MasterAutoDiaryGenerationConfigViewModel[]>(`${this.constants.apiURL()}/master/auto-diary-generation/config/`);
    }

    getMasterAutoDiaryGenerationConfig(id: number): Observable<MasterAutoDiaryGenerationConfigViewModel> {
        return this.httpClient.get<MasterAutoDiaryGenerationConfigViewModel>(`${this.constants.apiURL()}/master/auto-diary-generation/config/${id}`);
    }

    deleteMasterAutoDiaryConfig(id: number): Observable<void> {
        return this.httpClient.delete<void>(`${this.constants.apiURL()}/master/auto-diary-generation/config/delete/${id}`);
    }

    getMasterAutoDiaryRecipes(masterUserId: number): Observable<MasterAutoDiaryRecipesDto> {
        return this.httpClient.get<MasterAutoDiaryRecipesDto>(`${this.constants.dataURL()}/v1/master/recipes/${masterUserId}`);
    }

    createMasterAutoDiaryRecipes(dto: any): Observable<void> {
        return this.httpClient.post<void>(`${this.constants.apiURL()}/master/auto-diary-generation/config/`, dto);
    }

    updateMasterAutoDiaryRecipes(dto: any): Observable<void> {
        return this.httpClient.put<void>(`${this.constants.apiURL()}/master/auto-diary-generation/config/`, dto);
    }

}
